/**
 * Core routing component inside BrowserRouter.
 *
 * Uses useLocation() to detect URL changes. On each change:
 *   1. Match path against route table
 *   2. Check for unsaved changes (via useUnsavedChangesBlocker)
 *   3. Clear previous page state
 *   4. Update menus and section visibility
 *   5. Render the registered screen component
 *   6. Initialize PubNub
 */

import { Suspense, useEffect, useRef, useState, type ComponentType } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { setNavigateRef } from './navigate-ref';
import { matchRoute, resolveRoute } from './route-config';
import { getScreen } from './screen-registry';
import { useUnsavedChangesBlocker } from './useUnsavedChangesBlocker';
import { App } from '@/services/app-compat';
import { clearPageGlobalVarsAndCloseDialogs } from '@/services/page-cleanup';
import { removeModuleTypeAndAvailableSubmodules, getAvailableModules, getModuleType, currentUserHasPermission } from '@/utils/auth';
import { api } from '@/services/api-class';
import { navigateToModuleSelected } from '@/services/navigation';

const Mousetrap = (window as any).Mousetrap;

interface ScreenState {
  component: ComponentType<any>;
  props: Record<string, any>;
  /** Stable key that only changes when the screen should remount */
  screenKey: number;
}

export default function RouteHandler() {
  const location = useLocation();
  const navigate = useNavigate();
  const [screenState, setScreenState] = useState<ScreenState | null>(null);
  const prevRouteRef = useRef<string | null>(null);
  const screenKeyRef = useRef(0);

  // Store navigate ref so navigation.ts can use it
  useEffect(() => {
    setNavigateRef(navigate);
  }, [navigate]);

  // Activate unsaved changes blocker
  useUnsavedChangesBlocker();

  useEffect(() => {
    handleRouteChange(location.pathname, location.search, location.state);
  }, [location.pathname, location.search, location.key]);

  function handleRouteChange(hashPath: string, queryString?: string, navState?: any) {
    const w = window as any;

    // Match against route table
    const match = matchRoute(hashPath);
    if (!match) {
      navigate('/home', { replace: true });
      return;
    }

    const { route, params } = match;

    // Clear single-page mousetrap keyboard shortcuts
    if (typeof Mousetrap !== 'undefined') {
      Mousetrap.unbind('enter');
      if (App.IsNOU?.(App.MouseTrapBoundInputs) === false) {
        for (let i = 0; i < App.MouseTrapBoundInputs.length; i++) {
          Mousetrap(App.MouseTrapBoundInputs[i]).unbind('enter');
        }
      }
    }

    // Check for new frontend version
    if (typeof w.newVersionCheck === 'function') {
      w.newVersionCheck();
    }

    // Handle special routes
    if (route.special === 'signout') {
      handleSignOut();
      return;
    }
    if (route.special === 'login') {
      handleLogin();
      return;
    }
    if (route.special === 'home') {
      handleHome();
      return;
    }
    if (route.special === 'registration') {
      handleRegistration(params.code);
      return;
    }
    if (route.special === 'resetPassword') {
      handleResetPassword(params.code);
      return;
    }
    if (route.special === 'unsubscribe') {
      handleUnsubscribe();
      return;
    }

    // Normal route — resolve section/tab/fn from route config
    const resolved = resolveRoute(route, params);

    // Map query params → specific map screen variants
    if (resolved.tab === 'Map' && queryString) {
      const qp = new URLSearchParams(queryString);
      if (qp.has('address')) {
        params.address = qp.get('address')!;
        resolved.fn = 'NPSD_MapByAddress';
      } else if (qp.has('callCardId')) {
        params.callCardId = qp.get('callCardId')!;
        resolved.fn = `${resolved.section}_MapById`;
      }
    }

    const routeIdentity = `${resolved.section}|${resolved.tab}|${resolved.fn}`;
    const isSameRoute = routeIdentity === prevRouteRef.current;

    // Set menu (toolbar) for current section
    try { App.setMenu(resolved.section, resolved.tab); } catch (e) { /* ignore */ }

    // Only tear down the previous page when actually changing routes.
    // Same-route navigation (e.g. clicking the current menu item) skips
    // cleanup to avoid a visible flicker; the component remount via key
    // change is sufficient to reset screen state.
    if (!isSameRoute) {
      try { clearPageGlobalVarsAndCloseDialogs(); } catch (e) { /* ignore */ }
    }

    // Track current module
    try { App.setModuleTypeFromURL(resolved.section); } catch (e) { /* ignore */ }

    // Show/hide sections based on module (skip when staying on same route)
    if (!isSameRoute) {
      const divMainBody = document.getElementById('divMainBody');
      if (divMainBody?.classList.contains('hidden')) {
        divMainBody.classList.remove('hidden');
      }

      hideAllSections();

      if (resolved.section === 'CAD') {
        const sectionCAD = document.getElementById('SectionCAD');
        if (sectionCAD) showSection(sectionCAD);
      } else if (resolved.section === 'MDT') {
        const sectionMDT = document.getElementById('SectionMDT');
        if (sectionMDT) showSection(sectionMDT);
      } else if (resolved.section === 'ADMIN') {
        const sectionAdmin = document.getElementById('SectionAdmin');
        if (sectionAdmin) showSection(sectionAdmin);
      }
    }

    prevRouteRef.current = routeIdentity;

    // Save last route for crash recovery on mobile apps
    const wg = window as any;
    if (wg.isIOSApp || wg.isAndroidApp) {
      try { localStorage.setItem('LastRoute', location.pathname + location.search); } catch (_) {}
    }

    // Always bump the key so the component remounts (refresh behavior)
    screenKeyRef.current += 1;

    // If this is a "home" tab (e.g. /:section/home after module selection),
    // show the module's Home screen — not the Select Module screen.
    // (The Select Module screen is handled by handleHome() via special: 'home'.)
    if (resolved.tab === 'Home') {
      const homeEntry = getScreen('homeScreen');
      if (homeEntry) {
        const props = homeEntry.getProps?.(params, resolved.section) ?? {};
        setScreenState({ component: homeEntry.component, props, screenKey: screenKeyRef.current });
      }
      App.initPubNubAndPreloadAudio();
      return;
    }

    // Look up screen from registry
    if (resolved.fn) {
      let screenEntry = getScreen(resolved.fn);
      // Fallback: strip section prefix and try base name (e.g. CAD_Notes → Notes)
      if (!screenEntry) {
        const baseName = resolved.fn.replace(/^(CAD|MDT|ADMIN|PAYMENT)_/, '');
        if (baseName !== resolved.fn) {
          screenEntry = getScreen(baseName);
        }
      }
      if (screenEntry) {
        // Deep-link route guard: the menu hides items the user lacks READ for, but a
        // direct URL bypasses the menu. Block rendering and bounce to the module home
        // when the user lacks the screen's required authority.
        if (screenEntry.requiredPermission && !currentUserHasPermission(screenEntry.requiredPermission)) {
          navigateToModuleSelected(resolved.section);
          return;
        }
        const props = screenEntry.getProps?.(params, resolved.section, navState) ?? {};
        setScreenState({ component: screenEntry.component, props, screenKey: screenKeyRef.current });
        App.initPubNubAndPreloadAudio();
        return;
      }
    }

    // No registry entry — this should not happen if all screens are registered
    console.error(`No screen registered for route: ${resolved.fn}`);
    setScreenState(null);
  }

  function handleSignOut() {
    setScreenState(null);
    hideAllSections();
    const divMainBody = document.getElementById('divMainBody');
    if (divMainBody) divMainBody.classList.add('hidden');
    const signInSection = document.getElementById('SectionSignIn');
    if (signInSection) showSection(signInSection);
    App.signOutCleanup();
  }

  function showLoginScreen() {
    prevRouteRef.current = null;
    setScreenState(null);
    hideAllSections();
    const divMainBody = document.getElementById('divMainBody');
    if (divMainBody) divMainBody.classList.add('hidden');
    const signInSection = document.getElementById('SectionSignIn');
    if (signInSection) showSection(signInSection);
    App.signOutCleanup();
  }

  function handleLogin() {
    // In mobile apps, a crash/kill reloads "/" with the auth token still
    // in localStorage.  Redirect to /home so the session is preserved
    // instead of wiping it with signOutCleanup().
    const w = window as any;
    if (w.isIOSApp || w.isAndroidApp) {
      const authObj = typeof w.getAuthObject === 'function' ? w.getAuthObject() : null;
      if (authObj) {
        // Verify the session is still active before proceeding.
        // This avoids showing the select-module screen only to log out
        // moments later when the first API call returns 401.
        App.isLoggedIn((loggedIn: boolean) => {
          if (loggedIn) {
            navigate('/home', { replace: true });
          } else {
            showLoginScreen();
          }
        }, true, 'mobile-relaunch');
        return;
      }
    }

    showLoginScreen();
  }

  function handleHome() {
    const w = window as any;

    // Reset route tracking so the next navigation always performs full DOM setup
    // (e.g. after logout → login, prevRouteRef may still hold the old route)
    prevRouteRef.current = null;

    // Check if user is logged in before proceeding
    const authObj = typeof w.getAuthObject === 'function' ? w.getAuthObject() : null;
    if (!authObj) {
      // Not logged in — show sign-in section, hide main body
      setScreenState(null);
      hideAllSections();
      const signInSection = document.getElementById('SectionSignIn');
      if (signInSection) showSection(signInSection);
      const divMainBody = document.getElementById('divMainBody');
      if (divMainBody) divMainBody.classList.add('hidden');
      return;
    }

    // Determine which module to auto-select:
    // 1. Single-module users → their only module
    // 2. Mobile crash recovery → last used module (from ModuleTypeInfo in localStorage,
    //    which is saved during module selection and survives crashes reliably)
    const availableModules = getAvailableModules();
    const lastRoute = localStorage.getItem('LastRoute');
    const previousModule = getModuleType(); // from ModuleTypeInfo in localStorage
    const isMobileCrashRecovery = (w.isIOSApp || w.isAndroidApp) && previousModule;

    // First-login setup wizard placement: when the login response flags the wizard as
    // pending for this admin, route straight into Admin so the existing (admin-module-gated)
    // feature-intro fires — instead of dropping them on the module picker where they may
    // never find it. One-shot: clear the flag after routing so a later "Change Module"
    // still reaches the picker if the wizard was skipped.
    // See docs/decisions/2026-07-07-setup-wizard-placement.md
    const setupWizardPending = !!(authObj && authObj.setupWizardPending);

    let autoSelectModule: string | null = null;
    if (setupWizardPending && availableModules && availableModules.indexOf('admin') !== -1) {
      autoSelectModule = 'admin';
      authObj.setupWizardPending = false;
      if (typeof w.setAuthObject === 'function') w.setAuthObject(authObj);
    } else if (availableModules && availableModules.length === 1) {
      autoSelectModule = availableModules[0];
    } else if (isMobileCrashRecovery && availableModules && availableModules.indexOf(previousModule) !== -1) {
      autoSelectModule = previousModule;
    }

    if (autoSelectModule) {
      api.selectModule(
        autoSelectModule,
        (result: any) => {
          w.handleMdtModuleSelection(result, () => {
            w.selectModuleCallback(result);
            const section = (result.ModuleType || '').toUpperCase();

            // Restore last route after crash/reload on mobile apps
            localStorage.removeItem('LastRoute');
            if (lastRoute && lastRoute.toLowerCase().startsWith('/' + section.toLowerCase() + '/')) {
              navigate(lastRoute, { replace: true });
            } else {
              navigateToModuleSelected(section);
            }
          });
        },
        () => {
          // On error, fall through to show the select module screen
          localStorage.removeItem('LastRoute');
          showSelectModuleScreen();
        }
      );
      App.initPubNubAndPreloadAudio();
      return;
    }

    showSelectModuleScreen();
    App.initPubNubAndPreloadAudio();
  }

  function showSelectModuleScreen() {
    App.setMenu('Home');
    clearPageGlobalVarsAndCloseDialogs();
    App.setModuleTypeFromURL('Home');

    const divMainBody = document.getElementById('divMainBody');
    if (divMainBody?.classList.contains('hidden')) {
      divMainBody.classList.remove('hidden');
    }

    hideAllSections();
    const signInSection = document.getElementById('SectionSignIn');
    if (signInSection) signInSection.style.setProperty('display', 'none', 'important');
    const toolbar = document.getElementById('toolbar');
    if (toolbar) toolbar.style.display = 'none';

    const entry = getScreen('selectModule');
    if (entry) {
      screenKeyRef.current += 1;
      const props = entry.getProps?.({}, 'Home') ?? {};
      setScreenState({ component: entry.component, props, screenKey: screenKeyRef.current });
    }
  }

  function handleRegistration(code: string | undefined) {
    const w = window as any;
    removeModuleTypeAndAvailableSubmodules();
    clearInterval(App.isLoggedInInterval);
    clearInterval(App.trackLatLngInterval);

    // Check screen registry for registration screen
    const entry = getScreen('registration');
    if (entry) {
      screenKeyRef.current += 1;
      const props = entry.getProps?.({ code: code || '' }, '') ?? {};
      setScreenState({ component: entry.component, props, screenKey: screenKeyRef.current });
    } else {
      setScreenState(null);
      if (code) {
        App.registrationCode = code;
        w.OrnaReact.api.validateRegistrationCode(code, () => {});
      }
    }
  }

  function handleResetPassword(code: string | undefined) {
    removeModuleTypeAndAvailableSubmodules();
    clearInterval(App.isLoggedInInterval);
    clearInterval(App.trackLatLngInterval);

    const entry = getScreen('resetPassword');
    if (entry) {
      const props = entry.getProps?.({ code: code || '' }, '') ?? {};
      setScreenState({ component: entry.component, props, screenKey: screenKeyRef.current });
    } else {
      setScreenState(null);
      if (code) {
        App.resetPasswordCode = code;
        (window as any).OrnaReact.api.validatePasswordResetCode(code, () => {});
      }
    }
  }

  function handleUnsubscribe() {
    removeModuleTypeAndAvailableSubmodules();
    clearInterval(App.isLoggedInInterval);
    clearInterval(App.trackLatLngInterval);

    // UnsubscribeScreen reads unsubscribeId/clientId from the query string itself
    const entry = getScreen('unsubscribe');
    if (entry) {
      screenKeyRef.current += 1;
      setScreenState({ component: entry.component, props: {}, screenKey: screenKeyRef.current });
    } else {
      setScreenState(null);
    }
  }

  // Hide legacy #divContent when a React screen is active so its
  // .printCSS height rule doesn't create a scrollbar.
  useEffect(() => {
    const divContent = document.getElementById('divContent');
    if (!divContent) return;
    if (screenState) {
      divContent.style.display = 'none';
    } else {
      divContent.style.display = '';
    }
  }, [screenState]);

  // Always render a stable wrapper div so that screen DOM and ToastContainer
  // DOM (a fiber sibling via PubNubProvider) live in separate DOM subtrees.
  // Without this, React Router's startTransition (low priority) can race with
  // toast auto-dismiss (high priority): the toast DOM node used as the
  // insertBefore reference gets removed mid-transition, crashing the commit.
  const ScreenComponent = screenState?.component ?? null;
  return (
    <div className="route-handler-root">
      {ScreenComponent && (
        <Suspense fallback={<div className="kst-screen-loading" />}>
          <ScreenComponent key={screenState!.screenKey} {...screenState!.props} />
        </Suspense>
      )}
    </div>
  );
}

// ── DOM helpers (same as GeneralPathingHelper) ─────────────────────────

function hideAllSections() {
  document.querySelectorAll('section').forEach((el) => {
    (el as HTMLElement).style.setProperty('display', 'none', 'important');
  });
}

function showSection(el: HTMLElement) {
  el.style.setProperty('display', 'block', 'important');
}
